import {
	Button,
	ErrorMessageInput,
	Separator,
	Typography,
} from 'components/common';
import { InputEmail, InputPhone } from 'components/form';
import InputPassword from 'components/form/input-password/input-password';
import NextAuthProvidersEnum from 'const/auth';
import Icons from 'const/icons';
import AppRoutes from 'const/routes';
import Link from 'next/link';
import React from 'react';
import { passwordCriteria } from '@/const';
import { LayoutLogin } from '@/components/layout';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useAuth } from '@/hooks/useAuth';

interface SignUpProps {
	providers: NextAuthProvidersEnum[];
}

const Recaptcha = dynamic(
	() => import('@/components/common/recaptcha/ReCaptchaComponent'),
	{
		ssr: false,
	}
);

export const SignUpScreen: React.FC<SignUpProps> = () => {
	const {
		form,
		isLoading,
		isStaging,
		isDev,
		recaptchaValid,
		handleSubmitData,
		onChangeRecaptcha,
	} = useAuth();

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty, isValid },
		setValue,
		watch,
	} = form;

	// Inputs rules
	const rules = {
		email: {
			required: { value: true, message: 'This is required.' },
		},
		name: {
			required: { value: true, message: 'This is required.' },
		},
		phoneNumber: {
			required: { value: true, message: 'This is required.' },
		},
		password: {
			required: { value: true, message: 'This is required.' },
			maxLength: {
				value: 30,
				message: 'Your password must be 30 characters or less',
			},
			minLength: {
				value: 8,
				message:
					'Your password does not meet the minimum requirements. It must be at least 8 characters long and include one number, one uppercase letter, and one special character (_-:*).',
			},
			validate: (value: string) => {
				const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

				if (watch('confirmPassword') && value !== watch('confirmPassword')) {
					return 'The passwords do not match. Please ensure they are identical.';
				}
				if (!passwordRegex.test(value)) {
					return 'Your password does not meet the minimum requirements. It must be at least 8 characters long and include one number, one uppercase letter, and one special character (_-:*).';
				}

				return undefined;
			},
		},
		confirmPassword: {
			required: { value: false, message: 'This is required.' },
			maxLength: {
				value: 30,
				message: '',
			},
			minLength: {
				value: 8,
				message: '',
			},
			validate: (value: string) => {
				const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

				if (watch('confirmPassword') && value !== watch('confirmPassword')) {
					return '';
				}
				if (!passwordRegex.test(value)) {
					return '';
				}

				return undefined;
			},
		},
	};

	return (
		<LayoutLogin title="Get Started">
			<form
				className="w-full"
				onSubmit={handleSubmit((data) =>
					handleSubmitData(NextAuthProvidersEnum.CREDENTIALS, data)
				)}
			>
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 3xl:mb-6">
					<Button
						size="full"
						onClick={() => handleSubmitData(NextAuthProvidersEnum.GOOGLE)}
						disabled={isLoading}
						icon={Icons.google}
						iconLeft
						label="Sign up with Google"
						className="!py-4"
					/>
					<Button
						size="full"
						onClick={() => handleSubmitData(NextAuthProvidersEnum.MICROSOFT)}
						disabled={isLoading}
						icon={Icons.microsoft}
						iconLeft
						label="Sign up with Microsoft"
						className="!py-4"
					/>
				</div>
				<Separator text="Or" className="my-4 3xl:my-8" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 3xl:gap-6">
					<InputEmail
						name="email"
						title="Email"
						register={register}
						rules={rules.email}
						error={errors.email}
						setValueCustom={setValue}
					/>
					<InputPhone
						name="phoneNumber"
						title="Phone"
						register={register}
						rules={rules.phoneNumber}
						error={errors.phoneNumber}
						setValueInput={setValue}
					/>
					<InputPassword
						name="password"
						title="Password"
						register={register}
						validate={false}
						rules={rules.password}
						error={errors.password}
						hideErrorMessage
					/>
					<InputPassword
						name="confirmPassword"
						title="Confirm Password"
						register={register}
						validate={false}
						rules={rules.confirmPassword}
						error={errors.confirmPassword}
					/>
				</div>
				{errors.password && (
					<div
						className={clsx('mt-2', {
							'mb-6': (errors?.password?.message as string)?.includes(
								'(_-:*).'
							),
						})}
					>
						<ErrorMessageInput
							message={(errors?.password?.message as string) || ''}
						/>
					</div>
				)}
				<Typography
					type="regular-12"
					className={clsx('text-opacity-black mt-4 mb-4 2xl:mb-6', {
						hidden:
							typeof errors?.password?.message === 'string' &&
							errors?.password?.message?.includes('(_-:*).'),
					})}
				>
					{passwordCriteria}
				</Typography>
				<div className={clsx({ hidden: isStaging || isDev })}>
					<Recaptcha
						onChangeRecaptcha={onChangeRecaptcha}
						siteKey={process.env.NEXT_PUBLIC_API_RECAPTCHA || ''}
					/>
				</div>
				<Typography type="regular-16" className="text-black text-center my-6">
					By creating your account you are agreeing with <br /> our{' '}
					<Link href="/terms-of-use" target="_blank" className="underline">
						Terms of Use{' '}
					</Link>
					and{' '}
					<Link href="/privacy-policy" target="_blank" className="underline">
						Privacy Policy
					</Link>
					.
				</Typography>

				<Button
					type="submit"
					size="full"
					decoration="primary"
					label="Create an Account"
					loading={isLoading}
					className={clsx({
						'hover:bg-[#B18EE0] !bg-[#B18EE0] !border-[#B18EE0]':
							!isValid || !isDirty,
					})}
					disabled={isLoading || !recaptchaValid}
				/>

				<Typography
					type="regular-16"
					className="my-4 mb-24 md:mb-0 xl:mt-8 text-center"
				>
					{`Already have an account?`}
					<Link href={AppRoutes.CLIENTAPP_AUTH_SIGN_IN}>
						{' '}
						<span className="text-primary hover:opacity-60  font-medium lg:font-semibold">
							Sign in
						</span>
					</Link>
				</Typography>
			</form>
		</LayoutLogin>
	);
};
